import type { ClockFormat } from "@/lib/formatTime";
import type { BranchHour, Holiday } from "../schemas/salon.schemas";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * `09:00:00` to "9 am" or "09:00". The value is a wall-clock time the salon wrote, never a moment,
 * so no time zone applies and no date library is involved.
 */
export function formatWallClock(value: string | null, clock: ClockFormat): string {
  if (!value) return "";
  const [rawHours = "0", minutes = "00"] = value.split(":");
  const hours = Number(rawHours);
  if (clock === "24-hour") return `${String(hours).padStart(2, "0")}:${minutes}`;
  const suffix = hours < 12 ? "am" : "pm";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === "00" ? `${twelve} ${suffix}` : `${twelve}:${minutes} ${suffix}`;
}

function minutesOf(value: string | null): number | null {
  if (!value) return null;
  const [h = "0", m = "0"] = value.split(":");
  return Number(h) * 60 + Number(m);
}

/** The salon's weekday (0 Sunday), date and minutes past midnight right now, in its own zone. */
export function salonNow(
  timeZone: string,
  now: Date = new Date(),
): { day: number; date: string; minutes: number } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone,
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  );
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday ?? "");
  return {
    day,
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

export interface OpenStatus {
  open: boolean;
  /** "Open now · until 9 pm", "Opens at 9 am", "Closed today · opens tomorrow at 9 am". */
  label: string;
}

/**
 * Whether the salon is open at this moment and, either way, the next thing a customer needs to know.
 * A holiday closes the day whatever the week says. Null when the salon has published no hours at all.
 */
export function openStatus(
  hours: readonly BranchHour[],
  holidays: readonly Holiday[],
  clock: ClockFormat,
  timeZone: string,
  now: Date = new Date(),
): OpenStatus | null {
  if (hours.length === 0) return null;
  const today = salonNow(timeZone, now);
  const closedOn = new Set(holidays.map((holiday) => holiday.date));
  const rowFor = (day: number) => hours.find((hour) => hour.dayOfWeek === day);
  const isOpenDay = (day: number, offset: number) => {
    const row = rowFor(day);
    const date = shiftDate(today.date, offset);
    return row && !row.isClosed && row.opensAt && row.closesAt && !closedOn.has(date) ? row : null;
  };

  const todayRow = isOpenDay(today.day, 0);
  if (todayRow) {
    const opens = minutesOf(todayRow.opensAt) ?? 0;
    const closes = minutesOf(todayRow.closesAt) ?? 0;
    if (today.minutes >= opens && today.minutes < closes) {
      return { open: true, label: `Open now · until ${formatWallClock(todayRow.closesAt, clock)}` };
    }
    if (today.minutes < opens) {
      return { open: false, label: `Opens today at ${formatWallClock(todayRow.opensAt, clock)}` };
    }
  }

  for (let offset = 1; offset <= 7; offset++) {
    const day = (today.day + offset) % 7;
    const row = isOpenDay(day, offset);
    if (row) {
      const when = offset === 1 ? "tomorrow" : DAY_NAMES[day];
      return {
        open: false,
        label: `Closed now · opens ${when} at ${formatWallClock(row.opensAt, clock)}`,
      };
    }
  }
  return { open: false, label: "Closed now" };
}

/**
 * "Every day, 9 am – 9 pm" when the week is the same every day; null otherwise, and the full week is
 * listed instead.
 */
export function sameEveryDay(hours: readonly BranchHour[], clock: ClockFormat): string | null {
  if (hours.length !== 7) return null;
  const [first] = hours;
  if (!first || first.isClosed) return null;
  const same = hours.every(
    (hour) => !hour.isClosed && hour.opensAt === first.opensAt && hour.closesAt === first.closesAt,
  );
  return same
    ? `Every day, ${formatWallClock(first.opensAt, clock)} – ${formatWallClock(first.closesAt, clock)}`
    : null;
}

export interface WeekRow {
  /** "Mon – Tue", "Sun", or "Today · Wed". */
  label: string;
  /** "9 am – 8 pm" or "Closed". */
  value: string;
  today: boolean;
}

const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** The week as a customer reads a shop door: Monday first. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

/**
 * The opening week as the Design 2 hours card shows it (design note salon-booking-design-2.md H1):
 * Monday to Sunday, neighbouring days with the same hours folded into one row ("Thu – Sat"), and
 * today on a row of its own so it can be picked out.
 */
export function weekRows(
  hours: readonly BranchHour[],
  today: number,
  clock: ClockFormat,
): WeekRow[] {
  const valueOf = (day: number) => {
    const row = hours.find((hour) => hour.dayOfWeek === day);
    if (!row || row.isClosed || !row.opensAt || !row.closesAt) return "Closed";
    return `${formatWallClock(row.opensAt, clock)} – ${formatWallClock(row.closesAt, clock)}`;
  };
  const known = new Set(hours.map((hour) => hour.dayOfWeek));

  const rows: (WeekRow & { first: number; last: number })[] = [];
  for (const day of WEEK_ORDER.filter((d) => known.has(d))) {
    const value = valueOf(day);
    const previous = rows.at(-1);
    if (day !== today && previous && !previous.today && previous.value === value) {
      previous.last = day;
      continue;
    }
    rows.push({ label: "", value, today: day === today, first: day, last: day });
  }

  return rows.map(({ value, today: isToday, first, last }) => ({
    value,
    today: isToday,
    label: isToday
      ? `Today · ${SHORT_DAYS[first]}`
      : first === last
        ? (SHORT_DAYS[first] ?? "")
        : `${SHORT_DAYS[first]} – ${SHORT_DAYS[last]}`,
  }));
}

function shiftDate(date: string, days: number): string {
  const [y = 0, m = 1, d = 1] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
